import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, Output, EventEmitter, OnInit, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    AbstractControl, FormGroup
} from "@angular/forms";
import {
    EnumUtil, ILocalizableModel, Language, LocalizationUtil
} from "@shopliftr/common-js/data";
import { MatIcon } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";

import { ILocalizedFormGroup } from "../../interfaces/localized-form-group";
import { LanguagePipe } from "../../../pipes/language/language.pipe";
import { ButtonDirective } from "../../../application/directives/button/button.directive";
import { EnhancedFormDirective } from "../../enhanced-form.directive";

/**
 * The language tab strip for a Localized Domain Form.
 *
 * Not a `ControlValueAccessor` — it holds *which language is being edited*,
 * which is view state, not form data. But it is not a dumb emitter either:
 * it takes the form group as an input and performs the language switch
 * itself (enabling/disabling the non-selected localized groups), so no
 * consuming form re-implements that behavior.
 *
 * @example
 * ```html
 * <mgd-localization-selector #localizationSelector="mgdLocalizationSelector"
 *                            [formGroup]="formGroup" [model]="chain"
 *                            (selectedLanguage)="selectedLanguage = $event" />
 *
 * <input matInput [formControl]="localizationSelector.controlFor('name')" />
 * ```
 */
@Component({
    selector: "mgd-localization-selector",
    exportAs: "mgdLocalizationSelector",
    templateUrl: "./localization-selector.component.html",
    styleUrl: "./localization-selector.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LanguagePipe, MatIcon, MatButton, ButtonDirective, MatTooltip]
})
export class LocalizationSelectorComponent implements OnInit {

    /**
     * Display order for the non-English tabs - French before Spanish, by request, rather
     * than `Language`'s own declaration order (English, Spanish, French). A language not
     * listed here (none exist today) sorts before both rather than being silently dropped.
     */
    private static readonly _LANGUAGE_DISPLAY_ORDER: ReadonlyArray<Language> = [Language.French, Language.Spanish];

    /**
     * The host's whole form group. Reached by convention — no per-model configuration.
     *
     * Typed with a bare `Record<string, AbstractControl>` for `L` (rather than a per-model
     * shape) since this component works with any Localized Domain Form regardless of its
     * base or localized field names — only the runtime keys matter, not their static shape.
     */
    @Input({ required: true }) formGroup: FormGroup<ILocalizedFormGroup<Record<string, AbstractControl>>>;

    /** The localizable model, for the language list and the info banner's field names. */
    @Input({ required: true }) model: ILocalizableModel;

    /**
     * The host form's `EnhancedFormDirective`, for snapshot-based change detection behind the
     * tab indicators. Passed as an input rather than injected: in every consuming template this
     * component is the form's sibling, not its descendant (see the Chain form's own template,
     * where the selector opens at line 15 and `<form mgdEnhancedForm>` at line 19), so the
     * element injector cannot reach it.
     */
    @Input({ required: true }) enhancedForm: EnhancedFormDirective;

    /**
     * Languages whose tab shows a required marker - set by the host from its own rule (e.g. French while a
     * product is sold in Canada). The selector only renders it; it knows nothing about the rule.
     */
    @Input() requiredLanguages: ReadonlyArray<Language> = [];

    /**
     * Languages whose tab shows the error state - set by the host while its own rule fails for that language.
     * Hosts must pass stable array references (a static constant, not a fresh array per change-detection pass).
     */
    @Input() invalidLanguages: ReadonlyArray<Language> = [];

    /** The language whose fields the host should currently render live. */
    @Output() selectedLanguage = new EventEmitter<Language>();

    readonly Language = Language;

    selected: Language = Language.English;

    private readonly _cdRef = inject(ChangeDetectorRef);

    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Keeps the tabs' dirty indicators live under `OnPush`.
     *
     * Marking a control dirty is a flag change, not a value change - `valueChanges` stays
     * silent for it, and the field itself lives in the host's own template, outside this
     * component's view, so nothing else would prompt a re-check here either. `events` is
     * Angular's unified control-event stream and includes `PristineChangeEvent` alongside
     * value/status changes, so this one subscription covers every way `isDirty`/`isBaseDirty`
     * could go stale.
     */
    ngOnInit(): void {

        this.formGroup.events
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this._cdRef.markForCheck());
    }

    get languages(): ReadonlyArray<Language> {

        return EnumUtil.getValues(Language, [Language.English])
            .slice()
            .sort((a, b) => LocalizationSelectorComponent._LANGUAGE_DISPLAY_ORDER.indexOf(a)
                - LocalizationSelectorComponent._LANGUAGE_DISPLAY_ORDER.indexOf(b));
    }

    get textFieldNames(): ReadonlyArray<string> {

        return LocalizationUtil.localizationTextFields(this.model);
    }

    get imageFieldNames(): ReadonlyArray<string> {

        return LocalizationUtil.localizationImageFields(this.model);
    }

    /**
     * Whether the given language's group currently carries any content — queried live from the
     * form, not the snapshot, so it always reflects what's about to be saved. Feeds both the
     * "Has Localization" indicator directly and, via its own negation, "Pending Removal".
     *
     * @param language - The language to check
     */
    hasLocalization(language: Language): boolean {

        const group = this.formGroup.controls.localizations.controls[language];
        return Object.values(group.controls).some((control) => !!control.value);
    }

    /**
     * Whether the host marks the given language as required.
     *
     * @param language - The language to check
     */
    isRequired(language: Language): boolean {

        return this.requiredLanguages.includes(language);
    }

    /**
     * Whether the host marks the given language as failing its rule.
     *
     * @param language - The language to check
     */
    isInvalid(language: Language): boolean {

        return this.invalidLanguages.includes(language);
    }

    /**
     * Whether a persisted localization existed for this language at the last snapshot and its
     * group is now empty — saving will delete it. Must be checked before `isChanged`/
     * `hasLocalization` wherever this drives a mutually-exclusive indicator: clearing a
     * localization makes its group dirty by definition, so if `isChanged` won that race, this
     * state could never render at all.
     *
     * @param language - The language to check
     */
    isPendingRemoval(language: Language): boolean {

        return this._snapshotHasContent(language) && !this.hasLocalization(language);
    }

    /**
     * Whether the given language's group has an unsaved edit — sourced from
     * `EnhancedFormDirective`'s change snapshot (the same `ObjectUtil.getChanges` comparison
     * `canSubmit` itself is built on) rather than `AbstractControl.dirty`. `dirty` is a one-way
     * latch that never clears on its own: typing a character into a localized field and then
     * deleting it again would leave `dirty` permanently `true` even though the form has nothing
     * left to submit — disagreeing with `canSubmit` about whether anything actually changed.
     *
     * @param language - The language to check
     */
    isChanged(language: Language): boolean {

        return this.enhancedForm.isChangedAt(`localizations.${language}`);
    }

    /**
     * Whether the base group's own fields have an unsaved edit, for the English tab's indicator.
     * `localizations` is excluded so a localized-only edit doesn't also light up English. Same
     * snapshot-based source as {@link isChanged}, for the same reason.
     */
    get isBaseChanged(): boolean {

        return Object.keys(this.formGroup.controls)
            .some((key) => key !== "localizations" && this.enhancedForm.isChangedAt(key));
    }

    /**
     * Switches the selected language, disabling every other localized
     * group and enabling the one being switched to. The base group is
     * never touched.
     *
     * Switching to `Language.English` re-enables every localized group —
     * `English` never appears in {@link languages} itself, so "the one being
     * switched to" only disambiguates among the localized tabs; on the
     * default tab, no localized group is the non-selected one.
     *
     * @param language - The language to switch to
     */
    onLanguageClick(language: Language): void {

        for (const other of this.languages) {
            const group = this.formGroup.controls.localizations.controls[other];
            if (language === Language.English || other === language) {
                group.enable();
            }
            else {
                group.disable();
            }
        }

        this.selected = language;
        this.selectedLanguage.emit(language);
    }

    /**
     * The control the given localized field should currently be bound to:
     * the base control on the default tab, that language's mirrored control
     * otherwise.
     *
     * @param field - The localized field name (mirrors the base group's control key)
     */
    controlFor(field: string): AbstractControl {

        if (this.selected === Language.English) {
            // `ILocalizedFormGroup` only declares `localizations` - the base fields are
            // whatever the consuming form's own group happens to have, so this indexes by a
            // runtime key rather than a statically known one.
            return (this.formGroup.controls as unknown as Record<string, AbstractControl>)[field];
        }

        return this.formGroup.controls.localizations.controls[this.selected].controls[field];
    }

    /**
     * Whether `language`'s group carried any content at the last snapshot — the basis for
     * detecting a pending removal. Read lazily on every call, never cached at construction or
     * `ngOnInit`: this input is bound during the host's first update pass, which runs before
     * `EnhancedFormDirective.ngOnInit` sets its initial snapshot, and before the host's own
     * `ngAfterViewInit` re-snapshots via `populate()` — a value cached any earlier than that
     * would be computed against an empty pre-populate snapshot.
     */
    private _snapshotHasContent(language: Language): boolean {

        const snapshot = this.enhancedForm.snapshotValueAt(`localizations.${language}`) as Record<string, unknown> | undefined;
        return !!snapshot && Object.values(snapshot).some((value) => !!value);
    }
}
